import React, { useMemo } from 'react';
import useTimeline from '@src/hooks/useTimeline';
import { formatTimelineTime, pickTickInterval } from '@src/services/timeline';
import { MIN_LABEL_GAP_PX, MINOR_PER_MAJOR } from '@src/constants/timeline';
import { StyledMinorTick, StyledRuler, StyledTick } from './index.style';

/**
 * Time ruler. Ticks are generated by integer index (no float drift, clean
 * major test) and only within the visible range, so a long timeline never
 * renders thousands of nodes.
 */
const TimelineRuler = () => {
  const { pxPerSec, secToPx, durationSec, visibleRange } = useTimeline();

  const ticks = useMemo(() => {
    const step = pickTickInterval(pxPerSec, MIN_LABEL_GAP_PX) / MINOR_PER_MAJOR;
    const firstIndex = Math.max(0, Math.floor(visibleRange.fromSec / step));
    const lastIndex = Math.floor(
      Math.min(durationSec, visibleRange.toSec) / step,
    );

    const result = [];
    for (let index = firstIndex; index <= lastIndex; index += 1) {
      result.push({
        sec: index * step,
        isMajor: index % MINOR_PER_MAJOR === 0,
      });
    }
    return result;
  }, [pxPerSec, durationSec, visibleRange]);

  return (
    <StyledRuler>
      {ticks.map(({ sec, isMajor }) =>
        isMajor ? (
          <StyledTick
            key={sec}
            align={sec === 0 ? 'start' : 'center'}
            style={{ left: secToPx(sec) }}
          >
            {formatTimelineTime(sec)}
          </StyledTick>
        ) : (
          <StyledMinorTick key={sec} style={{ left: secToPx(sec) }} />
        ),
      )}
    </StyledRuler>
  );
};

export default TimelineRuler;
